import { useEffect, useState } from "react";
import { ImagePlus, LayoutGrid, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import MediaPicker from "../MediaPicker";
import CriticalPinDialog from "../CriticalPinDialog";

export default function CategoriesTab() {
    const [cats, setCats] = useState(null);
    const [editor, setEditor] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);
    const [q, setQ] = useState("");
    const [sortKey, setSortKey] = useState("name");

    const load = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openEditor = (c) =>
        setEditor({
            category_id: c?.category_id || null,
            name: c?.name || "",
            description: c?.description || "",
            image: c?.image || "",
            parent_id: c?.parent_id || "",
            isNew: !c,
        });

    const save = async () => {
        if (!editor.name.trim()) return toast.error("Kategori adı zorunludur");
        setBusy(true);
        try {
            const payload = { name: editor.name, description: editor.description, image: editor.image, parent_id: editor.parent_id || null };
            if (editor.isNew) {
                await api.post("/admin/categories", payload);
                toast.success("Kategori oluşturuldu — katalogda anında görünür");
            } else {
                await api.put(`/admin/categories/${editor.category_id}`, payload);
                toast.success("Kategori güncellendi");
            }
            setEditor(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (c, pin) => {
        setBusy(true);
        try {
            await api.delete(`/admin/categories/${c.category_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Kategori silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="categories-tab">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kategori ara..." className="h-9 w-56 text-sm" data-testid="category-search" />
                    <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="category-sort">
                        <option value="name">Ad (A-Z)</option>
                        <option value="name_desc">Ad (Z-A)</option>
                        <option value="count">Ürün Sayısı (çoktan aza)</option>
                    </select>
                </div>
                <Button onClick={() => openEditor(null)} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="category-new-button">
                    <Plus className="mr-2 h-4 w-4" /> Yeni Kategori
                </Button>
            </div>

            {!cats ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">{[...Array(6)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="space-y-6" data-testid="categories-grid">
                    {(() => {
                        const sorter = (a, b) => sortKey === "name" ? a.name.localeCompare(b.name, "tr") : sortKey === "name_desc" ? b.name.localeCompare(a.name, "tr") : (b.product_count || 0) - (a.product_count || 0);
                        const byId = Object.fromEntries((cats || []).map((c) => [c.category_id, c]));
                        const nq = q.toLowerCase();
                        const matchesQ = (c) => !nq || c.name.toLowerCase().includes(nq);
                        const visible = (cats || []).filter((c) =>
                            matchesQ(c) ||
                            (c.parent_id && byId[c.parent_id] && matchesQ(byId[c.parent_id])) ||
                            (!c.parent_id && (cats || []).some((s) => s.parent_id === c.category_id && matchesQ(s)))
                        );
                        const parents = visible.filter((c) => !c.parent_id).sort(sorter);
                        return parents.map((parent) => (
                        <div key={parent.category_id}>
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                                {[parent, ...visible.filter((c) => c.parent_id === parent.category_id).sort(sorter)].map((c) => (
                                    <div key={c.category_id} className={`group overflow-hidden rounded-2xl border bg-card shadow-sm ${c.parent_id ? "ml-4 border-l-4 border-l-terra-300" : ""}`} data-testid={`category-card-${c.category_id}`}>
                                        <div className="relative">
                                            {c.image ? (
                                                <img src={c.image} alt={c.name} className="aspect-[4/3] w-full object-cover" />
                                            ) : (
                                                <div className="flex aspect-[4/3] w-full items-center justify-center bg-muted">
                                                    <LayoutGrid className="h-8 w-8 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-stone-950/60 opacity-0 transition-opacity group-hover:opacity-100">
                                                <Button size="sm" variant="outline" className="h-8 bg-white/90 text-xs" onClick={() => openEditor(c)} data-testid={`category-edit-${c.category_id}`}>
                                                    <Pencil className="mr-1 h-3 w-3" /> Düzenle
                                                </Button>
                                                <Button size="sm" variant="outline" className="h-8 bg-white/90 text-xs text-destructive" onClick={() => setPinTarget(c)} data-testid={`category-delete-${c.category_id}`}>
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="p-3">
                                            <p className="truncate text-sm font-bold" title={c.parent_id && byId[c.parent_id] ? `${byId[c.parent_id].name} › ${c.name}` : c.name}>
                                                {c.parent_id && byId[c.parent_id] && <span className="font-normal text-muted-foreground">{byId[c.parent_id].name} › </span>}{c.name}
                                            </p>
                                            <div className="text-xs text-muted-foreground">
                                                <Badge variant="outline" className="mr-1 text-[10px]">{c.product_count} ürün</Badge> /{c.slug}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        ));
                    })()}
                    {cats && q && !(cats || []).some((c) => c.name.toLowerCase().includes(q.toLowerCase())) && (
                        <p className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="category-search-empty">"{q}" ile eşleşen kategori yok.</p>
                    )}
                </div>
            )}

            <Dialog open={!!editor} onOpenChange={() => setEditor(null)}>
                <DialogContent data-testid="category-editor-dialog">
                    {editor && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display">{editor.isNew ? "Yeni Kategori" : `Düzenle — ${editor.name}`}</DialogTitle>
                                <DialogDescription>Adres (slug) isimden otomatik üretilir.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <Input placeholder="Kategori adı * (örn: Kurutulmuş Sebzeler)" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="category-name-input" />
                                <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={editor.parent_id || ""} onChange={(e) => setEditor({ ...editor, parent_id: e.target.value })} data-testid="category-parent-select">
                                    <option value="">Ana kategori (üst kategori yok)</option>
                                    {(cats || []).filter((c) => !c.parent_id && c.category_id !== editor.category_id).map((c) => (
                                        <option key={c.category_id} value={c.category_id}>Alt kategorisi: {c.name}</option>
                                    ))}
                                </select>
                                <Textarea rows={2} placeholder="Açıklama (opsiyonel)" value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} data-testid="category-desc-input" />
                                <div>
                                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kategori Görseli</p>
                                    <div className="flex items-center gap-4 rounded-xl border border-dashed p-4">
                                        {editor.image ? (
                                            <img src={editor.image} alt="" className="h-20 w-20 rounded-lg object-cover" data-testid="category-image-preview" />
                                        ) : (
                                            <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-muted"><ImagePlus className="h-6 w-6 text-muted-foreground" /></div>
                                        )}
                                        <div>
                                            <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="category-pick-library">
                                                Ortam Kütüphanesi'nden Seç
                                            </Button>
                                            <p className="mt-1.5 text-xs text-muted-foreground">Yeni görsel için önce Ortam Kütüphanesi'ne yükleyin (otomatik WebP sıkıştırma).</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditor(null)}>Vazgeç</Button>
                                <Button onClick={save} disabled={busy} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="category-save-button">
                                    {busy ? "Kaydediliyor..." : editor.isNew ? "Oluştur" : "Kaydet"}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <MediaPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(url) => {
                    setEditor((e) => ({ ...e, image: url }));
                    setPickerOpen(false);
                }}
            />

            <CriticalPinDialog
                open={!!pinTarget}
                onClose={() => setPinTarget(null)}
                onSubmit={(pin) => remove(pinTarget, pin)}
                actionLabel={pinTarget ? `"${pinTarget.name}" kategorisini kalıcı olarak silme` : ""}
                busy={busy}
            />
        </div>
    );
}
